import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Briefcase, MagnifyingGlass, Info, MapPin } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";

const SOURCES = [
  { value: "arbeitsagentur", label: "Arbeitsagentur (mit Adresse)" },
  { value: "adzuna", label: "Adzuna (aggregiert Indeed, StepStone u.a.)" },
  { value: "jooble", label: "Jooble (aggregiert viele Portale)" },
];

const SOURCE_INFO = {
  arbeitsagentur: {
    name: "Bundesagentur für Arbeit",
    desc: "Offizielle Stellenbörse der Arbeitsagentur über deren kostenlose API. Liefert vollständige Adressen (Straße, PLZ, Ort) des Einsatzortes sowie das Veröffentlichungsdatum – die zuverlässigste und detailreichste Quelle.",
  },
  adzuna: {
    name: "Adzuna",
    desc: "Job-Aggregator, der Anzeigen aus vielen Portalen bündelt (u.a. Teile von Indeed & StepStone). Breite Abdeckung, liefert Firma + Ort und das Anzeigendatum.",
  },
  jooble: {
    name: "Jooble",
    desc: "Weiterer Aggregator über viele Jobportale. Hinweis: Jooble kann Server-Anfragen aus dieser Umgebung blockieren – falls eine Fehlermeldung erscheint, bitte eine andere Quelle nutzen.",
  },
};

function relativeDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  const label =
    days <= 0 ? "heute" : days === 1 ? "gestern" : `vor ${days} Tagen`;
  return { label, date: d.toLocaleDateString("de-DE") };
}

export default function Jobboersen() {
  const [selectedSources, setSelectedSources] = useState(["arbeitsagentur", "adzuna"]);
  const [keyword, setKeyword] = useState("Neueröffnung Gastronomie");
  const [location, setLocation] = useState("");
  const [radius, setRadius] = useState("25");
  const [enrich, setEnrich] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const toggleSource = (value) => {
    setSelectedSources((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
  };

  const start = async () => {
    if (!keyword.trim() || !location.trim()) {
      return toast.error("Bitte Stichwort und Ort/Region angeben");
    }
    if (selectedSources.length === 0) {
      return toast.error("Bitte mindestens eine Quelle wählen");
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/scrape/jobboards", {
        sources: selectedSources, keyword, location, radius: Number(radius), enrich,
      });
      setResult(data);
      (data.errors || []).forEach((e) => toast.warning(e));
      toast.success(`${data.total_offers} Anzeigen · ${data.found} Firmen · ${data.new} neue Leads · ${data.duplicates} Duplikate`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Abfrage fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold tracking-tight">Jobbörsen</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Neueröffnungen über Stellenanzeigen finden. Anzeigen werden pro Arbeitgeber
          zusammengefasst – ein Unternehmen wird als ein Lead übernommen.
        </p>
      </div>

      <div className="bg-[#002FA7]/5 border border-[#002FA7]/20 rounded-md p-4 mb-6 flex gap-3" data-testid="jb-info-box">
        <Info size={20} weight="fill" className="text-[#002FA7] shrink-0 mt-0.5" />
        <div className="text-sm text-zinc-700 space-y-2">
          <p className="font-medium">So funktioniert die Jobbörsen-Recherche</p>
          <p className="text-zinc-600">
            Wir durchsuchen Stellenbörsen nach deinem Stichwort (z.B. „Neueröffnung Gastronomie")
            im gewählten Ort/Umkreis. Alle Anzeigen desselben Arbeitgebers werden zu einem Lead
            zusammengefasst – so bekommst du das Unternehmen statt einzelner Jobs. Fehlt eine Adresse
            oder Website, ergänzen wir sie automatisch über Google Maps. Aktuell eingebunden:
          </p>
          <ul className="space-y-1.5">
            {Object.entries(SOURCE_INFO).map(([k, v]) => (
              <li key={k} className="flex gap-2">
                <span className="font-medium text-[#002FA7] shrink-0">{v.name}:</span>
                <span className="text-zinc-600">{v.desc}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md p-6 mb-6">
        <div className="mb-5">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Quellen (mehrere wählbar)</Label>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2">
            {SOURCES.map((s) => (
              <label key={s.value} className="flex items-center gap-2 cursor-pointer" data-testid={`jb-source-${s.value}`}>
                <Checkbox checked={selectedSources.includes(s.value)} onCheckedChange={() => toggleSource(s.value)} data-testid={`jb-source-checkbox-${s.value}`} />
                <span className="text-sm text-zinc-700">{s.label}</span>
              </label>
            ))}
          </div>
          {selectedSources.includes("jooble") && (
            <p className="text-xs text-amber-600 mt-2">
              Hinweis: Jooble blockiert evtl. Anfragen aus dieser Umgebung. Die anderen Quellen laufen dann trotzdem durch.
            </p>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <div className="lg:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Stichwort *</Label>
            <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} className="mt-1.5" data-testid="jb-keyword" onKeyDown={(e) => e.key === "Enter" && start()} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Ort / Region *</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="z.B. Hamburg" className="mt-1.5" data-testid="jb-location" onKeyDown={(e) => e.key === "Enter" && start()} />
          </div>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Umkreis (km)</Label>
              <Select value={radius} onValueChange={setRadius}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="jb-radius"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["0", "10", "25", "50", "100"].map((r) => <SelectItem key={r} value={r}>{r} km</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={start} disabled={loading} data-testid="jb-start-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm h-10">
              <MagnifyingGlass size={16} className="mr-2" />
              {loading ? "Suche…" : "Suchen"}
            </Button>
          </div>
        </div>
        <p className="text-xs text-zinc-400 mt-3">Es werden automatisch alle verfügbaren Ergebnisseiten durchsucht.</p>
        <label className="flex items-center gap-2 mt-3 cursor-pointer w-fit" data-testid="jb-enrich-toggle">
          <Checkbox checked={enrich} onCheckedChange={(v) => setEnrich(!!v)} data-testid="jb-enrich-checkbox" />
          <span className="text-sm text-zinc-700 flex items-center gap-1.5">
            <MapPin size={15} className="text-[#002FA7]" />
            Fehlende Firmendaten via Google Maps ergänzen (Adresse, Website, Telefon)
          </span>
        </label>
      </div>

      {result && (
        <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="jb-result">
          {(result.errors || []).length > 0 && (
            <div className="mb-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="jb-errors">
              {result.errors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}
          <div className="flex flex-wrap gap-3 mb-6">
            <Stat label="Anzeigen" value={result.total_offers} />
            <Stat label="Firmen" value={result.found} />
            <Stat label="Neu" value={result.new} accent />
            <Stat label="Duplikate" value={result.duplicates} muted />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div data-testid="jb-new-leads">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Neue Leads ({result.new})</div>
              <div className="border border-zinc-200 rounded-sm max-h-96 overflow-y-auto">
                {result.leads.length === 0 ? (
                  <div className="text-xs text-zinc-400 p-3">Keine neuen Leads.</div>
                ) : (
                  <table className="w-full text-xs">
                    <tbody>
                      {result.leads.map((l, i) => {
                        const rel = relativeDate(l.job_posted);
                        return (
                        <tr key={i} className="border-b border-zinc-100 last:border-0 align-top">
                          <td className="px-2 py-1.5 text-zinc-400 tabular-nums w-14">#{String(l.lead_no ?? "").padStart(4, "0")}</td>
                          <td className="px-2 py-1.5">
                            <div className="font-medium">{l.name}</div>
                            <div className="text-zinc-500">{l.street}{l.street && ", "}{l.plz} {l.city}</div>
                            {l.website && (
                              <a href={l.website} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline truncate max-w-[260px] inline-block align-bottom">
                                {l.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}
                              </a>
                            )}
                            <div className="text-zinc-400 truncate max-w-[280px]" title={(l.tags || []).join(", ")}>{(l.tags || []).length} Anzeige(n): {(l.tags || []).join(", ")}</div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="inline-block px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-600 text-[10px] font-medium">{l.source_label}</span>
                              {rel && (
                                <span className="text-[10px] text-zinc-400" title={`Anzeige vom ${rel.date}`} data-testid={`jb-lead-posted-${l.lead_no}`}>· aktualisiert {rel.label}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div data-testid="jb-duplicate-leads">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Duplikate ({result.duplicates})</div>
              <div className="border border-zinc-200 rounded-sm max-h-96 overflow-y-auto">
                {result.duplicate_leads.length === 0 ? (
                  <div className="text-xs text-zinc-400 p-3">Keine Duplikate.</div>
                ) : (
                  <table className="w-full text-xs">
                    <tbody>
                      {result.duplicate_leads.map((l, i) => (
                        <tr key={i} className="border-b border-zinc-100 last:border-0">
                          <td className="px-2 py-1.5 text-zinc-400 tabular-nums w-14">
                            {l.existing_lead_no ? `#${String(l.existing_lead_no).padStart(4, "0")}` : "—"}
                          </td>
                          <td className="px-2 py-1.5 text-zinc-700">
                            {l.name}
                            {l.source_label && <span className="ml-2 inline-block px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-500 text-[10px]">{l.source_label}</span>}
                          </td>
                          <td className="px-2 py-1.5 text-zinc-500 whitespace-nowrap">{l.plz} {l.city}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!result && !loading && (
        <div className="bg-white border border-dashed border-zinc-200 rounded-md p-10 text-center text-sm text-zinc-400">
          <Briefcase size={28} className="mx-auto mb-2 text-zinc-300" />
          Starte eine Suche, z.B. „Neueröffnung Gastronomie" in „Hamburg".
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, muted }) {
  return (
    <div className={`px-5 py-3 rounded-md border ${accent ? "border-[#002FA7] bg-[#002FA7]/5" : "border-zinc-200 bg-white"}`}>
      <div className={`font-heading text-2xl font-bold ${accent ? "text-[#002FA7]" : muted ? "text-zinc-400" : ""}`}>{value}</div>
      <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
}
